import { coordKey, distance } from '@tessera/engine';
// Upper-bound on tiles removed per move, used as divisor. A single slide can
// at best trigger a chain-2 annihilation; the annihilated group size is
// bounded by the number of same-color mergeable tiles connected after the
// slide, capped at 5 for typical boards (radius 3–4 with ≤6 colors).
const MAX_REMOVAL_PER_MOVE = 5;
// Per-color sanity check for the 'clear' objective. A color class is
// "stranded" — can never reach 0 tiles — if its (regular, charged) split
// matches one of these provably-dead patterns, regardless of board geometry:
//
//   total ∈ {1, 2}                : no group of size ≥3 can ever form.
//   total == 3 with charged == 0  : only option is a 3-regular merge →
//                                    (0r, 1c), then stuck at total=1.
//   total == 4 with charged == 0  : only options are 3-merge (→ 1+1c,
//                                    stuck) or 4-merge (→ 0+1c, stuck).
//                                    No annihilation path without a charged.
//
// Other splits at total ∈ {4, 5, 6+} are geometry-dependent — e.g. (3,1)
// at total=4 is solvable iff all four tiles form one adjacent group of 4
// (single-step annihilation of the whole 4-group). The heuristic can't
// cheaply check geometry, so we leave those to the search.
//
// Returning true means "this state is provably unreachable from goal".
// Detecting this lets IDA* prune a large slice of the search tree the
// moment a move strands a color.
export function isClearDeadEnd(board) {
    if (board.tiles.size === 0)
        return false; // already solved
    // Per-color totals split into regular and charged.
    const reg = new Map();
    const chg = new Map();
    for (const t of board.tiles.values()) {
        if (t.state.kind === 'charged')
            chg.set(t.color, (chg.get(t.color) ?? 0) + 1);
        else
            reg.set(t.color, (reg.get(t.color) ?? 0) + 1);
    }
    const colors = new Set([...reg.keys(), ...chg.keys()]);
    for (const c of colors) {
        const r = reg.get(c) ?? 0;
        const ch = chg.get(c) ?? 0;
        const total = r + ch;
        if (total === 1 || total === 2)
            return true;
        if (ch === 0 && (total === 3 || total === 4))
            return true;
    }
    return false;
}
// Per-color lower bound on merge-moves required to clear a single color class.
// Admissible — never overestimates. Derivation:
//   - Any merge operates on exactly ONE color. So the total cost is at least
//     the sum of per-color clear costs.
//   - If count == 0: 0 moves.
//   - If charged >= 1: a single annihilation move clears at most 5 tiles of
//     that color. Min moves = ceil(count / 5).
//   - If charged == 0 and count >= 5: the first merge consumes 3-5 tiles and
//     produces 1 charged (doesn't clear by itself — chainDepth=1, not
//     annihilation). Then subsequent moves can annihilate in 5-groups. So
//     min moves = 1 + ceil((count - 3) / 5) — one seeding move plus enough
//     annihilations to absorb the rest.
//   - Other splits (count in {1,2,3,4} with charged==0) are provably dead
//     and caught by isClearDeadEnd() before we get here.
//
// Comparison vs. the old heuristic ceil(N/5):
//   4 colors × 3 tiles each (N=12): old=3, new=4 (4 colors × 1 move each).
//   3 colors, counts [6,4,3] (N=13): old=3, new=4 (2+1+1).
//   5 colors × 4 tiles each (N=20): old=4, new=5.
//   1 color × 10 tiles, ch=0: old=2, new=3 (1 + ceil(7/5) = 1+2).
// Every case is >= the old bound, most are strictly tighter.
function minMovesForColor(count, charged) {
    if (count === 0)
        return 0;
    if (charged >= 1) {
        return Math.ceil(count / MAX_REMOVAL_PER_MOVE);
    }
    // charged == 0, count is {5, 6+} (smaller values would be dead-end pruned).
    if (count >= 5) {
        return 1 + Math.ceil((count - 3) / MAX_REMOVAL_PER_MOVE);
    }
    // Dead-end territory — isClearDeadEnd catches this earlier; defensive.
    return 1;
}
export function heuristic(board, objective) {
    switch (objective.kind) {
        case 'clear': {
            if (isClearDeadEnd(board))
                return Infinity;
            // Per-color sum dominates the old global ceil(N/5) because each color
            // requires its own merge-moves. See minMovesForColor comment above.
            const reg = new Map();
            const chg = new Map();
            for (const t of board.tiles.values()) {
                if (t.state.kind === 'charged')
                    chg.set(t.color, (chg.get(t.color) ?? 0) + 1);
                else
                    reg.set(t.color, (reg.get(t.color) ?? 0) + 1);
            }
            const colors = new Set([...reg.keys(), ...chg.keys()]);
            let total = 0;
            for (const c of colors) {
                const r = reg.get(c) ?? 0;
                const ch = chg.get(c) ?? 0;
                total += minMovesForColor(r + ch, ch);
            }
            return total;
        }
        case 'target': {
            let sum = 0;
            const byColor = new Map();
            for (const t of board.tiles.values()) {
                const arr = byColor.get(t.color) ?? [];
                arr.push(t.coord);
                byColor.set(t.color, arr);
            }
            for (const req of objective.targets) {
                const occ = board.tileAt.get(coordKey(req.coord));
                if (occ !== undefined) {
                    const t = board.tiles.get(occ);
                    if (t && t.color === req.color)
                        continue;
                }
                const candidates = byColor.get(req.color) ?? [];
                if (candidates.length === 0)
                    return Infinity;
                let min = Infinity;
                for (const c of candidates)
                    min = Math.min(min, distance(c, req.coord));
                sum += min;
            }
            return Math.ceil(sum / 2);
        }
        case 'pattern': {
            if (objective.pattern.length === 0)
                return 0;
            return 1;
        }
        case 'survival':
            return 0;
    }
}
export function isGoalState(board, objective) {
    switch (objective.kind) {
        case 'clear':
            return board.tiles.size === 0;
        case 'target': {
            for (const req of objective.targets) {
                const tid = board.tileAt.get(coordKey(req.coord));
                if (tid === undefined)
                    return false;
                const t = board.tiles.get(tid);
                if (!t || t.color !== req.color)
                    return false;
            }
            return true;
        }
        case 'pattern':
            return false;
        case 'survival':
            return false;
    }
}
